import {
  RiCheckboxCircleFill,
  RiErrorWarningFill,
  RiCheckboxBlankCircleLine,
  RiQuestionLine,
  RiAlertFill,
} from 'react-icons/ri';
import type { FC, ReactNode } from 'react';
import { _isNil, _isNotBlank } from '@/littledash';
import type { SyncEntityType } from './SyncConfiguration.model';
import styles from './EntityTabs.module.scss';

/** `locked`: a prerequisite entity isn't configured; `warning`: saveable, but a mapping is discouraged. */
export type EntityStatus = 'valid' | 'error' | 'empty' | 'locked' | 'warning';

/** Display labels for each entity, matching the chip text the user sees. */
export const ENTITY_LABELS: Record<SyncEntityType, string> = {
  study: 'Study',
  group: 'Groups',
  animal: 'Animals',
  sample: 'Samples',
  sample_detail_results: 'Sample Results',
  treatment: 'Disease & Treatments',
  measurement: 'Measurements',
  observation: 'Observations',
  dosage: 'Dosages',
};

interface EntityTabsProps {
  entityTypes: {
    required: Array<SyncEntityType>;
    optional: Array<SyncEntityType>;
  };
  selected: SyncEntityType;
  onSelect: (entity: SyncEntityType) => void;
  validity: Partial<Record<SyncEntityType, EntityStatus>>;
  /** Per-entity tooltip text, shown on hover (e.g. why a locked entity is disabled). */
  tooltips?: Partial<Record<SyncEntityType, string>>;
  testId?: string;
}

/**
 * Entity type tabs grouped into required and optional, each with a status badge and selected-state
 * highlight.
 */
const EntityTabs: FC<EntityTabsProps> = ({
  entityTypes,
  selected,
  onSelect,
  validity,
  tooltips = {},
  testId = 'entity-tabs',
}) => {
  const renderBadge = (entity: SyncEntityType): ReactNode => {
    const status = validity[entity];
    if (_isNil(status)) {
      return null;
    }

    switch (status) {
      case 'valid':
        return <RiCheckboxCircleFill className={styles.badge} data-status="valid" />;
      case 'error':
        return <RiErrorWarningFill className={styles.badge} data-status="error" />;
      case 'warning':
        return <RiAlertFill className={styles.badge} data-status="warning" />;
      case 'locked':
        return <RiQuestionLine className={styles.badge} data-status="locked" />;
      case 'empty':
        return <RiCheckboxBlankCircleLine className={styles.badge} data-status="empty" />;
      default:
        return null;
    }
  };

  const renderTab = (entity: SyncEntityType): ReactNode => {
    const isActive = entity === selected;
    const status = validity[entity];
    const isLocked = status === 'locked';
    const tabClass = `${styles.tab} ${isActive ? styles.active : ''} ${isLocked ? styles.locked : ''}`;
    const tooltip = tooltips[entity];

    return (
      <button
        key={entity}
        type="button"
        className={tabClass}
        onClick={() => onSelect(entity)}
        data-testid={`${testId}__tab--${entity}`}
        data-test-element={`tab--${entity}`}
        data-active={isActive}
        {...(_isNotBlank(tooltip) ? { 'data-tooltip-id': 'global-tooltip-id', 'data-tooltip-content': tooltip } : {})}
      >
        {renderBadge(entity)}
        <span className={styles.label}>{ENTITY_LABELS[entity]}</span>
      </button>
    );
  };

  return (
    <div className={styles.container} data-test-component="EntityTabs" data-testid={testId}>
      <div className={styles.group}>
        <span className={styles.groupLabel}>Required:</span>
        <div className={styles.tabs}>{entityTypes.required.map(renderTab)}</div>
      </div>

      <div className={styles.group}>
        <span className={styles.groupLabel}>Optional:</span>
        <div className={styles.tabs}>{entityTypes.optional.map(renderTab)}</div>
      </div>
    </div>
  );
};

export default EntityTabs;
